import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
    Sparkles,
    UserCheck,
    Coffee,
    Newspaper,
    Utensils,
    Car,
    Bike,
    Phone,
    Calendar,
    Plus,
    Search,
    Clock,
    ShieldCheck,
    CheckCircle2,
    QrCode as QrIcon,
    Trash2,
    Building2,
    User,
    Camera,
    X,
    RotateCcw,
    Shirt,
    Wrench,
    Check,
    Baby,
    FileSpreadsheet,
    Download
} from "lucide-react";
import ShareWhatsApp from "@/components/ShareWhatsApp";
import VisitorPhotoModal, { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";
import DailyStaffAttendanceReportModal from "@/components/DailyStaffAttendanceReportModal";

const SERVICE_TYPES = [
    { id: "Babysitter", label: "Babysitter / Nanny", icon: Baby, color: "bg-rose-50 text-rose-700 border-rose-200" },
    { id: "Maid", label: "Maid / Housekeeping", icon: Sparkles, color: "bg-pink-50 text-pink-700 border-pink-200" },
    { id: "Milkman", label: "Milkman", icon: Coffee, color: "bg-amber-50 text-amber-700 border-amber-200" },
    { id: "Newspaper", label: "Newspaper Delivery", icon: Newspaper, color: "bg-blue-50 text-blue-700 border-blue-200" },
    { id: "Cook", label: "Cook / Chef", icon: Utensils, color: "bg-orange-50 text-orange-700 border-orange-200" },
    { id: "Driver", label: "Driver", icon: Car, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    { id: "Car Cleaner", label: "Car Cleaner", icon: Car, color: "bg-teal-50 text-teal-700 border-teal-200" },
    { id: "Laundry", label: "Laundry / Ironing", icon: Shirt, color: "bg-purple-50 text-purple-700 border-purple-200" },
    { id: "Society Staff", label: "Society Staff", icon: Wrench, color: "bg-slate-50 text-slate-700 border-slate-200" },
    { id: "Other", label: "Other Daily Help", icon: UserCheck, color: "bg-gray-50 text-gray-700 border-gray-200" },
];

function getServiceMeta(serviceType) {
    return SERVICE_TYPES.find((s) => s.id === serviceType) || SERVICE_TYPES[0];
}

function formatTime(isoStr) {
    if (!isoStr) return "";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch {
        return "";
    }
}

function formatDate(isoStr) {
    if (!isoStr) return "";
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch {
        return "";
    }
}

function HelperQRModal({ helper, onClose }) {
    if (!helper) return null;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=350x350&data=${encodeURIComponent(helper.pass_code)}`;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl border border-nest-line w-full max-w-sm p-6 text-center shadow-2xl my-auto animate-scale-up" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Permanent Gate Pass</span>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-nest-alt text-nest-ink2 cursor-pointer">
                        <X size={16} />
                    </button>
                </div>

                <div className="my-4 p-4 rounded-2xl bg-nest-alt/50 border border-nest-line inline-block">
                    <img src={qrUrl} alt="Pass QR" className="h-44 w-44 mx-auto rounded-xl" />
                </div>

                <h3 className="font-display text-xl font-bold text-nest-ink">{helper.name}</h3>
                <p className="text-xs font-semibold text-nest-clay mt-0.5">{helper.service_type}</p>

                <div className="mt-4 p-3 rounded-xl bg-nest-alt border border-nest-line flex items-center justify-between">
                    <span className="text-xs text-nest-ink2 font-medium">Passcode</span>
                    <span className="font-mono text-lg font-bold tracking-widest text-nest-ink">{helper.pass_code}</span>
                </div>

                <p className="text-[11px] text-nest-ink3 mt-3">Guard can scan this QR or enter the 6-character code at the gate for 1-tap check-in.</p>

                <div className="mt-5 flex gap-2">
                    <ShareWhatsApp
                        phone={helper.phone}
                        label="Send to Staff"
                        text={`Hello ${helper.name}, your permanent gate entry pass for NeighborNest Society is *${helper.pass_code}*. Show this to the security guard for gate entry.`}
                    />
                    <button onClick={onClose} className="flex-1 nest-btn-primary text-xs py-2.5 cursor-pointer">
                        Done
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function AttendanceModal({ helper, onClose }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!helper || !isAdmin) return;
        setLoading(true);
        api.get(`/daily-help/logs?help_id=${helper.help_id}`)
            .then((r) => setLogs(r.data || []))
            .catch(() => setLogs([]))
            .finally(() => setLoading(false));
    }, [helper, isAdmin]);

    if (!helper || !isAdmin) return null;

    const exportHelperCsv = () => {
        if (!logs.length) return;
        const headers = ["Name", "Pass Code", "Service", "Date", "Entry Time", "Exit Time", "Duration", "Status", "Verified By"];
        const rows = logs.map((l) => [
            `"${(helper.name || "").replace(/"/g, '""')}"`,
            `"${helper.pass_code || ""}"`,
            `"${helper.service_type || ""}"`,
            `"${l.date || formatDate(l.entered_at)}"`,
            `"${formatTime(l.entered_at)}"`,
            `"${l.exited_at ? formatTime(l.exited_at) : "Currently Inside"}"`,
            `"${l.duration_str || ""}"`,
            `"${l.exited_at ? "Exited" : "Inside"}"`,
            `"${l.guard_name || "Gate Security"}"`
        ]);
        const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(r => r.join(","))].join("\r\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Attendance_${helper.name.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${logs.length} logs for ${helper.name}`);
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-3xl border border-nest-line w-full max-w-lg p-5 sm:p-6 shadow-2xl max-h-[85vh] sm:max-h-[90vh] flex flex-col my-auto animate-scale-up" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center pb-4 border-b border-nest-line">
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Gate Attendance Record</span>
                        <h3 className="font-display text-xl font-bold text-nest-ink">{helper.name}</h3>
                        <p className="text-xs text-nest-ink2">{helper.service_type} · Passcode: <span className="font-mono font-semibold">{helper.pass_code}</span></p>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-nest-alt text-nest-ink2 cursor-pointer">
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto py-4 divide-y divide-nest-line/60">
                    {loading && (
                        <div className="py-12 text-center text-xs text-nest-ink3 flex items-center justify-center gap-2">
                            <div className="h-4 w-4 rounded-full border-2 border-nest-clay border-t-transparent animate-spin" />
                            <span>Loading logs…</span>
                        </div>
                    )}
                    {!loading && logs.length === 0 && (
                        <div className="py-12 text-center text-xs text-nest-ink2">
                            No attendance history logged yet for this helper.
                        </div>
                    )}
                    {!loading &&
                        logs.map((log) => (
                            <div key={log.log_id} className="py-3 flex items-center justify-between text-xs">
                                <div>
                                    <p className="font-semibold text-nest-ink flex items-center gap-1.5">
                                        <Calendar size={12} className="text-nest-clay" />
                                        <span>{log.date || formatDate(log.entered_at)}</span>
                                    </p>
                                    <p className="text-nest-ink3 text-[11px] mt-0.5">
                                        Verified by {log.guard_name || "Security Gate"}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono font-semibold text-emerald-700">
                                        IN: {formatTime(log.entered_at) || "—"}
                                    </p>
                                    <p className="font-mono text-nest-ink2 text-[11px]">
                                        OUT: {log.exited_at ? formatTime(log.exited_at) : "Currently Inside"}
                                    </p>
                                </div>
                            </div>
                        ))}
                </div>

                <div className="pt-3 border-t border-nest-line flex items-center justify-between">
                    <button
                        onClick={exportHelperCsv}
                        disabled={logs.length === 0}
                        className="px-4 py-2 rounded-xl bg-nest-clay hover:bg-nest-clayDark text-white text-xs font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs active:scale-95"
                    >
                        <Download size={13} />
                        <span>Export CSV</span>
                    </button>
                    <button onClick={onClose} className="px-5 py-2 rounded-xl bg-nest-ink text-white text-xs font-semibold cursor-pointer">
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function UpdatePhotoModal({ helper, onClose, onPhotoUpdated }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    if (!helper) return null;

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (f) {
            setFile(f);
            setPreview(URL.createObjectURL(f));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Please choose or capture a photo first");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("photo", file);
            const r = await api.post(`/daily-help/${helper.help_id}/photo`, fd);
            toast.success(`Photo updated for ${helper.name}`);
            onPhotoUpdated(helper.help_id, r.data.photo_url);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to upload photo");
        } finally {
            setUploading(false);
        }
    };

    const currentSrc = preview || (helper.has_photo ? getVisitorPhotoSrc(helper) : null);

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-3xl border border-nest-line w-full max-w-sm p-6 text-center shadow-2xl my-auto animate-scale-up" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Staff Photo Record</span>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-nest-alt text-nest-ink2 cursor-pointer">
                        <X size={16} />
                    </button>
                </div>

                <h3 className="font-display text-xl font-bold text-nest-ink">{helper.name}</h3>
                <p className="text-xs font-semibold text-nest-clay mt-0.5">{helper.service_type} · Pass: {helper.pass_code}</p>

                <div className="my-5 flex flex-col items-center">
                    <div className="h-44 w-44 rounded-3xl overflow-hidden border-2 border-dashed border-nest-line bg-nest-alt/50 grid place-items-center relative shadow-inner">
                        {currentSrc ? (
                            <img src={currentSrc} alt={helper.name} className="h-full w-full object-cover" />
                        ) : (
                            <div className="text-center p-4 text-nest-ink3">
                                <Camera size={36} className="mx-auto mb-2 text-nest-clay opacity-80" />
                                <p className="text-xs font-medium text-nest-ink">No photo attached</p>
                                <p className="text-[10px] mt-0.5">Attach a photo for security verification</p>
                            </div>
                        )}
                    </div>

                    <label className="mt-4 cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-nest-alt border border-nest-line hover:border-nest-clay text-xs font-semibold text-nest-ink shadow-2xs transition">
                        <Camera size={14} className="text-nest-clay" />
                        <span>{file ? "Change Selected Photo" : "Take Photo / Choose File"}</span>
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleFile}
                            className="hidden"
                            data-testid="upload-photo-modal-input"
                        />
                    </label>
                    <p className="text-[10px] text-nest-ink3 mt-1.5">Opens device camera or photo gallery</p>
                </div>

                <div className="flex gap-2 pt-3 border-t border-nest-line">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-2.5 rounded-xl border border-nest-line text-xs font-semibold text-nest-ink hover:bg-nest-alt transition cursor-pointer"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!file || uploading}
                        className="flex-1 nest-btn-primary text-xs py-2.5 disabled:opacity-50 cursor-pointer"
                        data-testid="confirm-photo-upload-btn"
                    >
                        {uploading ? "Saving..." : "Save Photo"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function AddHelperModal({ open, onClose, onCreated, myFlat }) {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        service_type: "Babysitter",
        flats: myFlat ? myFlat : "",
        vehicle_type: "none",
        vehicle_no: "",
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Please enter helper's name");
            return;
        }
        setSaving(true);
        try {
            const flatsArr = form.flats
                .split(",")
                .map((f) => f.trim())
                .filter(Boolean);

            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim() || null,
                service_type: form.service_type,
                flats: flatsArr,
                vehicle_type: form.vehicle_type,
                vehicle_no: form.vehicle_no.trim() || null,
                status: "active",
            };
            const r = await api.post("/daily-help", payload);

            if (photoFile && r.data?.help_id) {
                try {
                    const fd = new FormData();
                    fd.append("photo", photoFile);
                    await api.post(`/daily-help/${r.data.help_id}/photo`, fd);
                    r.data.has_photo = true;
                    r.data.photo_path = `daily_help/${r.data.help_id}.jpg`;
                    r.data.photo_url = `/api/daily-help/${r.data.help_id}/photo`;
                } catch (photoErr) {
                    console.warn("Could not attach photo during registration:", photoErr);
                }
            }

            toast.success(`${form.name} registered with pass ${r.data.pass_code}`);
            onCreated(r.data);
            setPhotoFile(null);
            setPhotoPreview(null);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not register daily helper");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-3xl border border-nest-line w-full max-w-lg p-5 sm:p-7 shadow-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto my-auto animate-scale-up" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center pb-4 border-b border-nest-line">
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Society Staff Registry</span>
                        <h3 className="font-display text-2xl font-bold text-nest-ink mt-0.5">Register Daily Staff</h3>
                        <p className="text-xs text-nest-ink2 mt-1">Generates a permanent gate pass for babysitters, maids, milkmen, etc.</p>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-nest-alt text-nest-ink2 cursor-pointer">
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={submit} className="mt-5 space-y-4">
                    {/* Staff Photo Upload */}
                    <div className="p-3.5 rounded-2xl bg-nest-alt/40 border border-nest-line flex items-center gap-4">
                        <div className="h-16 w-16 rounded-2xl border border-nest-line bg-white flex-shrink-0 overflow-hidden grid place-items-center shadow-2xs">
                            {photoPreview ? (
                                <img src={photoPreview} alt="" className="h-full w-full object-cover" />
                            ) : (
                                <Camera size={22} className="text-nest-ink3" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block">
                                Staff Photo (Optional)
                            </label>
                            <p className="text-[11px] text-nest-ink2 mt-0.5">Used by security guards at the gate to verify identity.</p>
                            <div className="mt-2 flex items-center gap-2">
                                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white border border-nest-line text-xs font-semibold text-nest-ink hover:border-nest-clay transition shadow-2xs">
                                    <Camera size={12} className="text-nest-clay" />
                                    <span>{photoFile ? "Change Photo" : "Take Photo / Choose"}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handlePhotoChange}
                                        className="hidden"
                                        data-testid="add-helper-photo-input"
                                    />
                                </label>
                                {photoFile && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPhotoFile(null);
                                            setPhotoPreview(null);
                                        }}
                                        className="text-[11px] text-nest-err font-medium hover:underline"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block mb-1">
                            Staff / Helper Name *
                        </label>
                        <input
                            required
                            placeholder="e.g. Pooja Patil (Babysitter) or Sunita Shinde (Maid)"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-nest-alt/50 border border-nest-line rounded-xl px-4 py-2.5 text-sm focus:border-nest-clay focus:bg-white outline-none transition"
                            data-testid="helper-name-input"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block mb-1">
                                Service Type *
                            </label>
                            <select
                                value={form.service_type}
                                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                                className="w-full bg-nest-alt/50 border border-nest-line rounded-xl px-4 py-2.5 text-sm focus:border-nest-clay focus:bg-white outline-none transition cursor-pointer"
                                data-testid="helper-service-select"
                            >
                                {SERVICE_TYPES.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block mb-1">
                                Mobile Phone
                            </label>
                            <input
                                placeholder="+91 98765 43210"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="w-full bg-nest-alt/50 border border-nest-line rounded-xl px-4 py-2.5 text-sm focus:border-nest-clay focus:bg-white outline-none transition"
                                data-testid="helper-phone-input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block mb-1">
                            Assigned Flats (Comma-separated)
                        </label>
                        <input
                            placeholder="e.g. A-101, B-202, C-601 (leave empty for society-wide services)"
                            value={form.flats}
                            onChange={(e) => setForm({ ...form, flats: e.target.value })}
                            className="w-full bg-nest-alt/50 border border-nest-line rounded-xl px-4 py-2.5 text-sm focus:border-nest-clay focus:bg-white outline-none transition"
                            data-testid="helper-flats-input"
                        />
                        <p className="text-[11px] text-nest-ink3 mt-1">
                            Milkmen & newspaper delivery can be left empty for society-wide access.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block mb-1">
                                Vehicle Type
                            </label>
                            <select
                                value={form.vehicle_type}
                                onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                                className="w-full bg-nest-alt/50 border border-nest-line rounded-xl px-4 py-2.5 text-sm focus:border-nest-clay focus:bg-white outline-none transition cursor-pointer"
                            >
                                <option value="none">No vehicle (On foot / Cycle)</option>
                                <option value="bike">Motorcycle / Scooter</option>
                                <option value="car">Car / Van</option>
                            </select>
                        </div>

                        {form.vehicle_type !== "none" && (
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-nest-ink3 block mb-1">
                                    Vehicle Number
                                </label>
                                <input
                                    placeholder="e.g. MH12AB1234"
                                    value={form.vehicle_no}
                                    onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })}
                                    className="w-full bg-nest-alt/50 border border-nest-line rounded-xl px-4 py-2.5 text-sm uppercase font-mono focus:border-nest-clay focus:bg-white outline-none transition"
                                />
                            </div>
                        )}
                    </div>

                    <div className="pt-4 border-t border-nest-line flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 rounded-xl border border-nest-line text-xs font-semibold hover:bg-nest-alt transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="nest-btn-primary text-xs px-6 py-2.5 flex items-center gap-2 cursor-pointer shadow-sm hover:shadow"
                            data-testid="helper-submit-btn"
                        >
                            <QrIcon size={15} className="text-emerald-400" />
                            <span>{saving ? "Generating QR Code…" : "Register & Generate QR Code"}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}

export default function DailyHelp() {
    const { user } = useAuth();
    const isStaff = user?.role === "admin" || user?.role === "guard";
    const isAdmin = user?.role === "admin";
    const myFlat = user?.tower && user?.flat_no ? `${user.tower}-${user.flat_no}` : "";

    const [helpers, setHelpers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [serviceFilter, setServiceFilter] = useState("all"); // "all" | "inside" | "my" | serviceType
    const [addOpen, setAddOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [activeQR, setActiveQR] = useState(null);
    const [activeAttendance, setActiveAttendance] = useState(null);
    const [activePhoto, setActivePhoto] = useState(null);
    const [photoUploadTarget, setPhotoUploadTarget] = useState(null);

    const handlePhotoUpdated = (helpId, newPhotoUrl) => {
        setHelpers((prev) =>
            prev.map((h) =>
                h.help_id === helpId
                    ? { ...h, has_photo: true, photo_url: newPhotoUrl, photo_path: `daily_help/${helpId}.jpg` }
                    : h
            )
        );
    };

    const loadHelpers = async () => {
        try {
            const r = await api.get("/daily-help");
            setHelpers(r.data || []);
        } catch {
            setHelpers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHelpers();
        const timer = setInterval(loadHelpers, 7000);
        return () => clearInterval(timer);
    }, []);

    const toggleCheckIn = async (helper) => {
        try {
            if (helper.is_inside) {
                await api.post(`/daily-help/${helper.help_id}/check-out`);
                toast.success(`${helper.name} checked out from society`);
            } else {
                await api.post(`/daily-help/${helper.help_id}/check-in`);
                toast.success(`${helper.name} checked in at gate`);
            }
            await loadHelpers();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const deleteHelper = async (helper) => {
        const confirmMsg = `Are you sure you want to permanently remove ${helper.name} (${helper.service_type || 'Staff'}) from society records?\n\nTheir gate entry pass, flat assignments, and attendance logs will be cleared.`;
        if (!window.confirm(confirmMsg)) return;
        try {
            await api.delete(`/daily-help/${helper.help_id}`);
            toast.success(`✓ ${helper.name} permanently removed from society`);
            setHelpers((prev) => prev.filter((h) => h.help_id !== helper.help_id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove staff profile");
        }
    };

    // Filter helpers
    const filtered = helpers.filter((h) => {
        if (serviceFilter === "inside" && !h.is_inside) return false;
        if (serviceFilter === "my" && !h.assigned_to_me) return false;
        if (serviceFilter !== "all" && serviceFilter !== "inside" && serviceFilter !== "my") {
            if (h.service_type !== serviceFilter) return false;
        }

        if (search.trim()) {
            const q = search.toLowerCase().trim();
            const flatsStr = (h.flats || []).join(" ").toLowerCase();
            const matches =
                (h.name && h.name.toLowerCase().includes(q)) ||
                (h.phone && h.phone.toLowerCase().includes(q)) ||
                (h.pass_code && h.pass_code.toLowerCase().includes(q)) ||
                (h.service_type && h.service_type.toLowerCase().includes(q)) ||
                (h.vehicle_no && h.vehicle_no.toLowerCase().includes(q)) ||
                flatsStr.includes(q);
            if (!matches) return false;
        }
        return true;
    });

    const insideCount = helpers.filter((h) => h.is_inside).length;
    const myCount = helpers.filter((h) => h.assigned_to_me).length;

    return (
        <div className="animate-fade-in max-w-7xl mx-auto" data-testid="daily-help-page">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-nest-line">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-nest-clay">
                            Daily Services & Household Staff
                        </span>
                        {insideCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 animate-pulse">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                {insideCount} Inside Society
                            </span>
                        )}
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-1">
                        Daily Help & Staff
                    </h1>
                    <p className="text-nest-ink2 text-sm mt-1 max-w-xl">
                        Permanent gate passes, live presence tracking, and instant check-in for maids, milkmen, cooks, drivers, and paper delivery.
                    </p>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap">
                    {isAdmin && (
                        <button
                            onClick={() => setReportOpen(true)}
                            className="bg-white border border-nest-line hover:border-nest-clay text-nest-ink font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 transition shadow-2xs hover:bg-nest-alt active:scale-95 cursor-pointer text-sm"
                            data-testid="attendance-report-btn"
                            title="Generate staff attendance report (Admin Only)"
                        >
                            <FileSpreadsheet size={16} className="text-nest-clay" />
                            <span>Attendance Report</span>
                        </button>
                    )}
                    <button
                        onClick={() => setAddOpen(true)}
                        className="nest-btn-primary flex items-center gap-2 cursor-pointer"
                        data-testid="add-helper-btn"
                    >
                        <Plus size={16} /> Register Staff / Maid
                    </button>
                </div>
            </div>

            {/* Quick Metrics & Filter Tabs */}
            <div className="mt-6 flex flex-col gap-4">
                {/* Search Bar */}
                <div className="relative">
                    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-nest-ink3" />
                    <input
                        placeholder="Search by name, phone, passcode (DH-1021), flat (A-101), or vehicle..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-white border border-nest-line rounded-2xl pl-10 pr-4 py-3 text-sm focus:border-nest-clay outline-none transition shadow-2xs"
                        data-testid="daily-help-search"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-nest-ink3 hover:text-nest-ink"
                        >
                            <X size={15} />
                        </button>
                    )}
                </div>

                {/* Filter Pills */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
                    <button
                        onClick={() => setServiceFilter("all")}
                        className={`px-3 py-1.5 rounded-xl font-medium transition flex-shrink-0 ${
                            serviceFilter === "all"
                                ? "bg-nest-clay text-white font-semibold shadow-2xs"
                                : "bg-white border border-nest-line text-nest-ink2 hover:bg-nest-alt"
                        }`}
                        data-testid="filter-all"
                    >
                        All Staff ({helpers.length})
                    </button>

                    <button
                        onClick={() => setServiceFilter("inside")}
                        className={`px-3 py-1.5 rounded-xl font-medium transition flex-shrink-0 flex items-center gap-1.5 ${
                            serviceFilter === "inside"
                                ? "bg-emerald-700 text-white font-semibold shadow-2xs"
                                : "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                        }`}
                        data-testid="filter-inside"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Inside Now ({insideCount})
                    </button>

                    {!isStaff && myFlat && (
                        <button
                            onClick={() => setServiceFilter("my")}
                            className={`px-3 py-1.5 rounded-xl font-medium transition flex-shrink-0 ${
                                serviceFilter === "my"
                                    ? "bg-nest-ink text-white font-semibold shadow-2xs"
                                    : "bg-white border border-nest-line text-nest-ink2 hover:bg-nest-alt"
                            }`}
                            data-testid="filter-my"
                        >
                            My Flat ({myCount})
                        </button>
                    )}

                    {["Babysitter", "Maid", "Milkman", "Newspaper", "Cook", "Driver", "Car Cleaner"].map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setServiceFilter(cat)}
                            className={`px-3 py-1.5 rounded-xl font-medium transition flex-shrink-0 ${
                                serviceFilter === cat
                                    ? "bg-nest-ink text-white font-semibold shadow-2xs"
                                    : "bg-white border border-nest-line text-nest-ink2 hover:bg-nest-alt"
                            }`}
                            data-testid={`filter-${cat.toLowerCase()}`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Grid of Helpers */}
            <div className="mt-6">
                {loading && (
                    <div className="py-20 text-center text-sm text-nest-ink3 flex flex-col items-center justify-center gap-3">
                        <div className="h-6 w-6 rounded-full border-2 border-nest-clay border-t-transparent animate-spin" />
                        <span>Loading society staff…</span>
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div className="p-12 text-center bg-white rounded-3xl border border-nest-line shadow-2xs">
                        <UserCheck size={36} className="mx-auto text-nest-ink3 opacity-40 mb-3" />
                        <h4 className="font-display text-lg font-semibold text-nest-ink">No staff members found</h4>
                        <p className="text-xs text-nest-ink2 mt-1">
                            {search || serviceFilter !== "all"
                                ? "Try adjusting your search or category filter."
                                : "Register your maids, milkman, cook, or driver to issue permanent gate passes."}
                        </p>
                        <button
                            onClick={() => {
                                setSearch("");
                                setServiceFilter("all");
                            }}
                            className="mt-4 text-xs font-semibold text-nest-clay hover:underline inline-flex items-center gap-1"
                        >
                            <RotateCcw size={12} /> Reset filters
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((h) => {
                        const meta = getServiceMeta(h.service_type);
                        const ServiceIcon = meta.icon;
                        const photoSrc = h.has_photo ? getVisitorPhotoSrc(h) : null;

                        return (
                            <div
                                key={h.help_id}
                                className="bg-white rounded-3xl border border-nest-line p-5 shadow-2xs hover:shadow-md transition flex flex-col justify-between relative overflow-hidden"
                                data-testid={`helper-card-${h.help_id}`}
                            >
                                {/* Top Row */}
                                <div>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="relative group flex-shrink-0">
                                                {photoSrc ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setActivePhoto(h)}
                                                        className="h-14 w-14 rounded-2xl overflow-hidden border border-nest-line flex-shrink-0 hover:ring-2 hover:ring-nest-clay transition cursor-pointer block shadow-2xs"
                                                        title="Click to view full photo"
                                                        data-testid={`view-photo-${h.help_id}`}
                                                    >
                                                        <img src={photoSrc} alt={h.name} className="h-full w-full object-cover group-hover:scale-105 transition" />
                                                    </button>
                                                ) : (
                                                    <div className="h-14 w-14 rounded-2xl bg-nest-alt border border-nest-line flex-shrink-0 grid place-items-center text-nest-ink font-bold text-base">
                                                        {h.name.slice(0, 1)}
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPhotoUploadTarget(h);
                                                    }}
                                                    className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white border border-nest-line shadow-xs grid place-items-center text-nest-ink2 hover:text-nest-clay hover:border-nest-clay transition cursor-pointer"
                                                    title={photoSrc ? "Change Photo" : "Add Photo"}
                                                    data-testid={`upload-photo-btn-${h.help_id}`}
                                                >
                                                    <Camera size={11} className={photoSrc ? "text-nest-ink2" : "text-nest-clay"} />
                                                </button>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <h3 className="font-display font-bold text-base text-nest-ink leading-tight">
                                                        {h.name}
                                                    </h3>
                                                    {h.assigned_to_me && (
                                                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-nest-clay/10 text-nest-clay">
                                                            My Flat
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.color}`}>
                                                        <ServiceIcon size={11} /> {h.service_type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status Badge */}
                                        <span
                                            className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0 flex items-center gap-1 ${
                                                h.is_inside
                                                    ? "bg-emerald-600 text-white shadow-2xs"
                                                    : "bg-nest-alt text-nest-ink3 border border-nest-line"
                                            }`}
                                        >
                                            <span className={`h-1.5 w-1.5 rounded-full ${h.is_inside ? "bg-white animate-ping" : "bg-neutral-400"}`} />
                                            {h.is_inside ? "Inside" : "Out"}
                                        </span>
                                    </div>

                                    {/* Passcode & QR action */}
                                    <div className="mt-4 p-2.5 rounded-2xl bg-nest-alt/60 border border-nest-line flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2">
                                            <span className="text-nest-ink3 text-[10px] font-bold uppercase tracking-wider">Pass:</span>
                                            <span className="font-mono font-bold text-nest-ink tracking-wider text-sm">{h.pass_code}</span>
                                        </div>
                                        <button
                                            onClick={() => setActiveQR(h)}
                                            className="text-[11px] font-semibold text-nest-clay hover:underline inline-flex items-center gap-1"
                                            data-testid={`qr-btn-${h.help_id}`}
                                        >
                                            <QrIcon size={12} /> QR Pass
                                        </button>
                                    </div>

                                    {/* Assigned Flats */}
                                    <div className="mt-3 text-xs">
                                        <span className="text-nest-ink3 block text-[10px] uppercase font-bold tracking-wider mb-1">
                                            Servicing Flats:
                                        </span>
                                        {h.flats && h.flats.length > 0 ? (
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {h.flats.map((fl) => (
                                                    <span
                                                        key={fl}
                                                        className={`font-medium px-2 py-0.5 rounded-md text-[11px] border ${
                                                            fl === myFlat
                                                                ? "bg-nest-clay text-white font-bold border-nest-clay"
                                                                : "bg-white text-nest-ink border-nest-line"
                                                        }`}
                                                    >
                                                        {fl}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-nest-ink2 text-[11px] italic">Society-wide service</span>
                                        )}
                                    </div>

                                    {/* Presence Details */}
                                    <div className="mt-3 text-[11px] text-nest-ink3 flex items-center gap-1.5">
                                        <Clock size={12} className="text-nest-clay flex-shrink-0" />
                                        {h.is_inside ? (
                                            <span className="text-emerald-700 font-medium">
                                                Checked in at {formatTime(h.last_entry_at)}
                                            </span>
                                        ) : h.last_exit_at ? (
                                            <span>Last checked out {formatDate(h.last_exit_at)} at {formatTime(h.last_exit_at)}</span>
                                        ) : (
                                            <span>No gate activity today</span>
                                        )}
                                    </div>

                                    {/* Vehicle Info if present */}
                                    {h.vehicle_no && (
                                        <div className="mt-2 text-xs flex items-center gap-1 font-mono font-semibold text-nest-ink bg-white border border-nest-line px-2 py-0.5 rounded-md inline-flex">
                                            {h.vehicle_type === "car" ? <Car size={12} className="text-nest-clay" /> : <Bike size={12} className="text-nest-clay" />}
                                            <span>{h.vehicle_no}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Actions Bottom Bar */}
                                <div className="mt-5 pt-3 border-t border-nest-line flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        {h.phone && (
                                            <>
                                                <a
                                                    href={`tel:${String(h.phone).replace(/[^0-9+]/g, "")}`}
                                                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-nest-clay text-white hover:bg-nest-clayDark transition"
                                                    title={`Call ${h.phone}`}
                                                    data-testid={`call-helper-${h.help_id}`}
                                                >
                                                    <Phone size={12} />
                                                    <span>Call</span>
                                                </a>
                                                <a
                                                    href={`https://wa.me/${String(h.phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${h.name}, regarding your service in our society.`)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition"
                                                    title="WhatsApp"
                                                >
                                                    <span className="text-xs font-bold">WA</span>
                                                </a>
                                            </>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => setActiveAttendance(h)}
                                                className="text-xs font-medium text-nest-ink2 hover:text-nest-ink hover:underline px-2 py-1.5 cursor-pointer"
                                                title="View Attendance Logs (Admin Only)"
                                                data-testid={`attendance-logs-btn-${h.help_id}`}
                                            >
                                                Attendance
                                            </button>
                                        )}
                                    </div>

                                    {/* Action buttons: Remove Staff (Left Society) + Check In/Out */}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {(isAdmin || h.assigned_to_me) && (
                                            <button
                                                type="button"
                                                onClick={() => deleteHelper(h)}
                                                className="text-xs font-semibold px-2.5 py-1.5 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 transition cursor-pointer flex items-center gap-1 shadow-2xs"
                                                title={`Permanently remove ${h.name} from society records`}
                                                data-testid={`delete-helper-btn-${h.help_id}`}
                                            >
                                                <Trash2 size={13} />
                                                <span>Remove</span>
                                            </button>
                                        )}

                                        {isStaff && (
                                            <button
                                                type="button"
                                                onClick={() => toggleCheckIn(h)}
                                                className={`text-xs font-bold px-3 py-1.5 rounded-xl transition cursor-pointer shadow-2xs ${
                                                    h.is_inside
                                                        ? "bg-neutral-800 text-white hover:bg-black"
                                                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                                                }`}
                                                data-testid={`checkin-btn-${h.help_id}`}
                                            >
                                                {h.is_inside ? "Check Out" : "Check In"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modals */}
            <AddHelperModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onCreated={(newHelper) => setHelpers((prev) => [newHelper, ...prev])}
                myFlat={myFlat}
            />
            <UpdatePhotoModal
                helper={photoUploadTarget}
                onClose={() => setPhotoUploadTarget(null)}
                onPhotoUpdated={handlePhotoUpdated}
            />
            <HelperQRModal helper={activeQR} onClose={() => setActiveQR(null)} />
            {isAdmin && <AttendanceModal helper={activeAttendance} onClose={() => setActiveAttendance(null)} />}
            <VisitorPhotoModal visitor={activePhoto} onClose={() => setActivePhoto(null)} />
            {isAdmin && (
                <DailyStaffAttendanceReportModal
                    open={reportOpen}
                    onClose={() => setReportOpen(false)}
                    helpers={helpers}
                    onViewPhoto={(h) => setActivePhoto(h)}
                />
            )}
        </div>
    );
}
